import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { ReactElement, useCallback, useMemo } from 'react';
import { InternalLink, TripAdvisorRating } from '@hotelplan/graphql.types';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { getTargetPageData } from '@hotelplan/libs.router-link-utils';
import { track } from '@hotelplan/libs.tracking-events';
import usePatchRouteWithSearchQuery from 'components/core/search/usePatchRouteWithSearchQuery';
import { ITabsItem } from 'components/core/tabs/Tabs.types';
import PdpClimate from './climate/PdpClimate';
import PdpDescription from './description/PdpDescription';
import PdpItinerary from './itinerary/PdpItinerary';
import PdpMap from './map/PdpMap';
import { PdpTabsKey } from './Pdp.types';
import PdpOrl from './PdpOrl';
import PdpRating from './rating/PdpRating';
import PdpRegion from './region/PdpRegion';
import PdpResort from './resort/PdpResort';

export interface IUsePdpTabsProps {
  content?: {
    overview?: {
      taRating?: TripAdvisorRating;
      productName?: string;
    };
  };
  pdpOrlUrl?: InternalLink;
  pdpDescriptionUrl?: InternalLink;
  pdpRatingUrl?: InternalLink;
  pdpMapUrl?: InternalLink;
  pdpResortUrl?: InternalLink;
  pdpRegionUrl?: InternalLink;
  pdpClimateUrl?: InternalLink;
  pdpItineraryUrl?: InternalLink;
}

const trackTabSwitchClick = (language: string, label: string) => {
  track({
    event: 'switch_tab',
    action: 'switch',
    label,
    category: undefined,
    component: 'tabs',
    position: 'tabs section',
    language,
    _clear: true,
  });
};

export const usePdpTabs = (data: IUsePdpTabsProps, isRoundtrip?: boolean) => {
  const { mobile } = useDeviceType();
  const { query } = useRouter();
  const [t, { language }] = useTranslation('pdp');

  const { s, metasearch, page, geoObjectId } = query as {
    s?: string | undefined;
    metasearch?: string | undefined;
    page?: string | undefined;
    geoObjectId?: string | undefined;
  };

  const orlLink = usePatchRouteWithSearchQuery(data?.pdpOrlUrl, [
    's',
    'metasearch',
  ]);

  const tabItems = useMemo<ITabsItem<PdpTabsKey>[]>(() => {
    const availableTabs = [
      PdpTabsKey.Orl,
      PdpTabsKey.Description,
      PdpTabsKey.Map,
      PdpTabsKey.Region,
      PdpTabsKey.Climate,
    ];

    if (data?.content.overview.taRating?.rating > 0) {
      availableTabs.push(PdpTabsKey.Rating);
    }

    if (isRoundtrip) {
      availableTabs.push(PdpTabsKey.Itinerary);
    }

    if (!isRoundtrip) {
      availableTabs.push(PdpTabsKey.Resort);
    }

    const hiddenQueryParams: Record<string, string> = {
      s,
      metasearch,
      page,
      geoObjectId,
    };

    return [
      {
        tab: PdpTabsKey.Orl,
        routerProps: getTargetPageData(orlLink, {
          geoObjectId,
          page,
        }),
        label: t('pdp:tab.orl'),
        component: <PdpOrl productName={data?.content.overview.productName} />,
        onClick: () =>
          trackTabSwitchClick(language, t('pdp:tab.orl', { lng: 'de' })),
      },
      {
        tab: PdpTabsKey.Description,
        routerProps: getTargetPageData(
          data?.pdpDescriptionUrl,
          hiddenQueryParams
        ),
        label: t(`pdp:tab.description`),
        component: <PdpDescription isRoundtrip={isRoundtrip} />,
        onClick: () =>
          trackTabSwitchClick(
            language,
            t('pdp:tab.description', { lng: 'de' })
          ),
      },
      {
        tab: PdpTabsKey.Itinerary,
        routerProps: getTargetPageData(
          data?.pdpItineraryUrl,
          hiddenQueryParams
        ),
        label: t('pdp:tab.itinerary'),
        component: <PdpItinerary />,
        auxiliary: mobile,
        onClick: () =>
          trackTabSwitchClick(language, t('pdp:tab.itinerary', { lng: 'de' })),
      },
      {
        tab: PdpTabsKey.Rating,
        routerProps: getTargetPageData(data?.pdpRatingUrl, hiddenQueryParams),
        label: t(`pdp:tab.rating.${mobile ? 'mobile' : 'desktop'}`),
        component: <PdpRating />,
        onClick: () =>
          trackTabSwitchClick(
            language,
            t(`pdp:tab.rating.${mobile ? 'mobile' : 'desktop'}`, { lng: 'de' })
          ),
      },
      {
        tab: PdpTabsKey.Map,
        routerProps: getTargetPageData(data?.pdpMapUrl, hiddenQueryParams),
        label: t(`pdp:tab.map`),
        component: <PdpMap />,
        onClick: () =>
          trackTabSwitchClick(language, t('pdp:tab.map', { lng: 'de' })),
      },
      {
        tab: PdpTabsKey.Resort,
        routerProps: getTargetPageData(data?.pdpResortUrl, hiddenQueryParams),
        label: t('pdp:tab.resort'),
        component: <PdpResort />,
        auxiliary: mobile,
        onClick: () =>
          trackTabSwitchClick(language, t('pdp:tab.resort', { lng: 'de' })),
      },
      {
        tab: PdpTabsKey.Region,
        routerProps: getTargetPageData(data?.pdpRegionUrl, hiddenQueryParams),
        label: t('pdp:tab.region'),
        component: <PdpRegion />,
        auxiliary: mobile,
        onClick: () =>
          trackTabSwitchClick(language, t('pdp:tab.region', { lng: 'de' })),
      },
      {
        tab: PdpTabsKey.Climate,
        routerProps: getTargetPageData(data?.pdpClimateUrl, hiddenQueryParams),
        label: t('pdp:tab.climate'),
        component: <PdpClimate />,
        auxiliary: mobile,
        onClick: () =>
          trackTabSwitchClick(language, t('pdp:tab.climate', { lng: 'de' })),
      },
    ]
      .filter(item => !!item.routerProps)
      .filter(item => availableTabs.includes(item.tab));
  }, [s, metasearch, data, t, mobile, language]);

  const getComponentByTab = useCallback(
    (tabKey: PdpTabsKey): ReactElement => {
      return tabItems.filter(item => item.tab === tabKey)[0]?.component;
    },
    [tabItems]
  );

  return { tabItems, getComponentByTab };
};
